import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  CustomCard,
  PageHeading,
  HeaderColumn,
  BootstrapTable,
  LastHeaderColumn,
  FirstHeaderColumn
} from "../common-styling";
import { ACCESS_CONFIG } from "../strings";
import TableLoader from "../loader/table-loader";
import { useDialog } from "../../hooks/useDialog";
import { hasAccess } from "../../services/access";
import TemplateTableRow from "./template-table-row";
import { showError } from "../../services/toast-service";
import AdvancedFilters from "../table-filter/advanced-filter";
import CustomPagination from "../pagination/custom-pagination";
import { getAllTemplates } from "../../services/api-declaration";
import { funnelFilterConfig } from "../funnels-table/filter-config";
import { useTranslation } from "react-i18next";

const TemplatesTable = ({ asSubComponent = false }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginateOpts, setPaginateOpt] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { showCreateNewDialogBox } = useDialog();

  const getTemplates = async () => {
    try {
      setIsLoading(true);
      const params = {};
      for (const [key, value] of searchParams.entries()) {
        params[key] = value;
      }
      const response = await getAllTemplates(params);
      const data = response.data.data;
      setTemplates(data.rows);
      setPaginateOpt({
        totalPages: data.totalPages,
        hasPrevPage: data.hasPrevPage,
        hasNextPage: data.hasNextPage,
        totalDocs: data.totalDocs,
        rowsOnPage: data.rows.length
      });
    } catch (error) {
      console.log("ERROR: ", error);
      showError(
        error.response.data.message ||
          t("toastMessage.errorInGetting", {
            what: t("common.template", { count: 2 })
          })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleQueryParams = (property, value) => {
    searchParams.set(property, value);
    setSearchParams(searchParams);
  };
  const params = useParams();
  const handleModal = () =>
    showCreateNewDialogBox({
      url: "/dashboard/templates/funnel-templates/new",
      customer: params.id,
      modaltype: "common.template"
    });
  const getFunnelsCb = useCallback(
    getTemplates,
    // eslint-disable-next-line
    [searchParams]
  );
  useEffect(() => {
    getFunnelsCb();
  }, [getFunnelsCb]);
  return (
    <div>
      <PageHeading>{t("sidebar.templates")}</PageHeading>
      <div className="mt-3">
        {!asSubComponent && (
          <AdvancedFilters
            queryName="category"
            components={funnelFilterConfig}
            pageType="templateType"
            heading={t("sidebar.templates")}
            pageName="name"
            btnText={t("common.createNew.title", {
              name: t("common.template").toLowerCase()
            })}
            handleModal={handleModal}
            hideAddNew={!hasAccess(ACCESS_CONFIG.ADMIN_TIER)}
            shouldDisplayResourceType
          />
        )}
      </div>
      <CustomCard className="mt-4 mb-3 p-3 rounded-0">
        <BootstrapTable responsive>
          <thead>
            <tr>
              <FirstHeaderColumn>{t("common.templateName")}</FirstHeaderColumn>
              <HeaderColumn>{t("common.type")}</HeaderColumn>
              <HeaderColumn>{t("templateTable.lastModified")}</HeaderColumn>
              {hasAccess(ACCESS_CONFIG.ADMIN_TIER) && <LastHeaderColumn />}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableLoader colSpan={4} />
            ) : (
              <TemplateTableRow
                templates={templates}
                getTemplates={getTemplates}
              />
            )}
          </tbody>
        </BootstrapTable>
      </CustomCard>
      <CustomPagination
        paginateOpts={paginateOpts}
        activeTab={Number(searchParams.get("pageIndex")) || 1}
        select={(val) => handleQueryParams("pageIndex", val)}
      />
    </div>
  );
};
export default TemplatesTable;
